import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Paper,
  InputAdornment, IconButton, Link as MuiLink, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Card, CardContent
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Email, Lock, Login as LoginIcon,
  Key, Close, ContentCopy, CheckCircle
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password states
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [copied, setCopied] = useState(false);

  // ✅ ONLY ADMIN USER (with shopId)
  const ADMIN_USER = {
    id: 1,
    name: 'Admin User',
    email: 'admin@raath.com',
    password: 'admin123',
    role: 'admin',
    // shopId set dynamically by ensureShopId()  // ✅ Admin uses same shop as app
    pages: ['dashboard', 'pos', 'tables', 'menu', 'orders', 'kitchen', 'inventory', 'accounts', 'reports', 'staff', 'settings', 'backup'],
    permissions: ['create_order', 'edit_order', 'delete_order', 'cancel_bill', 'apply_discount', 'split_bill', 'merge_tables', 'refund', 'manage_menu', 'manage_inventory', 'manage_staff', 'view_reports', 'manage_settings', 'manage_backup', 'view_accounts', 'manage_expenses', 'print_bill', 'print_kot', 'update_kitchen_status', 'view_kot'],
  };

  // ==================== HELPER: Get Staff from Storage ====================
  const getStaffFromStorage = () => {
    try {
      const data = localStorage.getItem('raath_staff');
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading staff:', e);
      return [];
    }
  };

  // ==================== HELPER: Get Roles from Storage ====================
  const getRolesFromStorage = () => {
    try {
      const data = localStorage.getItem('raath_roles');
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading roles:', e);
      return [];
    }
  };

  // ==================== HELPER: Find Staff User ====================
  const findStaffUser = (email, password) => {
    const staffList = getStaffFromStorage();
    const roles = getRolesFromStorage();

    const staffMember = staffList.find(s => {
      if (s.email && s.email.toLowerCase() === email.toLowerCase()) {
        const passwordMatch = s.password === password || s.pin_code === password;
        if (passwordMatch && s.is_active !== false) {
          return true;
        }
      }
      return false;
    });

    if (!staffMember) return null;

    const roleInfo = roles.find(r => r.id === staffMember.role);

    // ✅ FIX: Get shopId from staff data or localStorage (don't generate new)
    const shopId = staffMember.shopId || localStorage.getItem('raath_shop_id');

    return {
      id: staffMember.id,
      name: staffMember.name,
      email: staffMember.email,
      role: staffMember.role,
      shopId: shopId,  // ✅ Include shopId
      pages: roleInfo?.pages || ['dashboard'],
      permissions: roleInfo?.permissions || [],
      is_staff: true,
      staffData: staffMember
    };
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleForgotOpen = () => {
    setForgotOpen(true);
    setForgotEmail('');
    setForgotError('');
    setFoundUser(null);
    setCopied(false);
  };

  const handleForgotClose = () => {
    setForgotOpen(false);
    setForgotEmail('');
    setForgotError('');
    setFoundUser(null);
    setCopied(false);
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    setFoundUser(null);
    setCopied(false);

    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }

    // ✅ Check ADMIN first
    if (forgotEmail.trim().toLowerCase() === ADMIN_USER.email.toLowerCase()) {
      setFoundUser({
        name: ADMIN_USER.name,
        email: ADMIN_USER.email,
        password: ADMIN_USER.password,
        role: ADMIN_USER.role
      });
      return;
    }

    // ✅ Check staff from localStorage
    const staffList = getStaffFromStorage();
    const staffMember = staffList.find(s => 
      s.email && s.email.toLowerCase() === forgotEmail.trim().toLowerCase()
    );

    if (staffMember) {
      setFoundUser({
        name: staffMember.name,
        email: staffMember.email,
        password: staffMember.password || staffMember.pin_code || 'N/A',
        role: staffMember.role
      });
      setForgotLoading(false);
      return;
    }

    setForgotLoading(true);
    try {
      let user = null;

      if (window.electronAPI?.getUserByEmail) {
        const result = await window.electronAPI.getUserByEmail(forgotEmail.trim().toLowerCase());
        user = result.success ? result.data : null;
      }

      if (!user) {
        const users = JSON.parse(localStorage.getItem('raath_users') || '[]');
        user = users.find(u => u.email === forgotEmail.trim().toLowerCase());
      }

      if (!user) {
        setForgotError('No account found with this email address');
        setForgotLoading(false);
        return;
      }

      setFoundUser({
        name: user.name,
        email: user.email,
        password: user.password || user.password_hash || 'N/A',
        role: user.role
      });
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError('Failed to retrieve account. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (foundUser?.password) {
      navigator.clipboard.writeText(foundUser.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ✅ FIX: Helper to ensure shopId is set (ROBUST)
  const ensureShopId = (userData) => {
    let shopId = userData.shopId;

    // Reject invalid shopIds
    const isInvalid = (id) => !id || id === 'shop_default' || id === 'shop_admin_default' || id === 'null' || id === 'undefined';

    // Step 1: Check userData.shopId
    if (isInvalid(shopId)) {
      // Step 2: Try localStorage
      shopId = localStorage.getItem('raath_shop_id');
    }

    // Step 3: If still invalid, generate new
    if (isInvalid(shopId)) {
      shopId = `shop_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('raath_shop_id', shopId);
      console.log(`[Login] Generated new shopId: ${shopId}`);
    }

    // Step 4: Always sync to localStorage
    localStorage.setItem('raath_shop_id', shopId);

    return {
      ...userData,
      shopId
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // ============================================================
      // STEP 1: Check ADMIN_USER
      // ============================================================
      if (form.email === ADMIN_USER.email && form.password === ADMIN_USER.password) {
        const { password, ...userWithoutPassword } = ADMIN_USER;
        const userWithShop = ensureShopId(userWithoutPassword);  // ✅ Ensure shopId
        login(userWithShop);
        navigate('/dashboard');
        setLoading(false);
        return;
      }

      // ============================================================
      // STEP 2: Check STAFF from localStorage (Settings → Staff)
      // ============================================================
      const staffUser = findStaffUser(form.email, form.password);
      if (staffUser) {
        console.log('[Login] Staff found:', staffUser);
        const userWithShop = ensureShopId(staffUser);  // ✅ Ensure shopId
        login(userWithShop);
        redirectUser(userWithShop);
        setLoading(false);
        return;
      }

      // ============================================================
      // STEP 3: Try Electron API
      // ============================================================
      let result = null;
      if (window.electronAPI?.verifyUser) {
        const apiResult = await window.electronAPI.verifyUser(form.email, form.password);
        if (apiResult) {
          result = apiResult;
        }
      }

      // ============================================================
      // STEP 4: Fallback: Check localStorage 'raath_users'
      // ============================================================
      if (!result) {
        const users = JSON.parse(localStorage.getItem('raath_users') || '[]');
        const localUser = users.find(
          u => u.email === form.email && (u.password === form.password || u.password_hash === form.password)
        );
        if (localUser) {
          const { password, password_hash, ...userWithoutPassword } = localUser;
          const userWithShop = ensureShopId(userWithoutPassword);  // ✅ Ensure shopId
          login(userWithShop);
          redirectUser(userWithShop);
          setLoading(false);
          return;
        }
      }

      if (result) {
        const userWithShop = ensureShopId(result);  // ✅ Ensure shopId
        login(userWithShop);
        redirectUser(userWithShop);
        setLoading(false);
        return;
      }

      setError('Invalid email or password');
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Redirect user based on role
  const redirectUser = (user) => {
    const role = user.role;
    const pages = user.pages || [];

    console.log('[Login] Redirecting user:', { role, pages, shopId: user.shopId });

    if (pages.includes('dashboard')) {
      navigate('/dashboard');
    } else if (pages.includes('pos')) {
      navigate('/pos');
    } else if (pages.includes('kitchen')) {
      navigate('/kitchen');
    } else if (pages.length > 0) {
      navigate('/' + pages[0]);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      p: 2
    }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <LoginIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your RAATH POS account
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            disabled={loading}
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Password / PIN"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={handleChange}
            disabled={loading}
            required
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <MuiLink
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={handleForgotOpen}
              sx={{ color: 'primary.main', fontWeight: 500 }}
            >
              Forgot Password?
            </MuiLink>
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            OR
          </Typography>
        </Divider>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <MuiLink
              component={Link}
              to="/signup"
              underline="hover"
              fontWeight="bold"
              sx={{ color: 'success.main' }}
            >
              Create Account
            </MuiLink>
          </Typography>
        </Box>

        {/* ✅ ADMIN QUICK LOGIN */}
        <Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: '#e8f5e9', 
            borderRadius: 2, 
            textAlign: 'center',
            border: '1px solid',
            borderColor: '#4caf50'
          }}
        >
          <Typography variant="caption" color="#2e7d32" display="block" fontWeight="bold" sx={{ mb: 1 }}>
            👑 ADMIN LOGIN
          </Typography>

          <Button
            size="small"
            variant="contained"
            color="success"
            sx={{ 
              textTransform: 'none',
              fontWeight: 'bold',
              borderRadius: 2
            }}
            onClick={() => {
              setForm({ email: ADMIN_USER.email, password: ADMIN_USER.password });
            }}
          >
            Login as Admin
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Click to auto-fill admin credentials
          </Typography>
        </Box>

        {/* Staff Login Info */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="caption" color="primary.dark" display="block" sx={{ fontWeight: 500 }}>
            👤 Staff Login: Use email and password/PIN set in Settings → Staff
          </Typography>
        </Box>
      </Paper>

      {/* FORGOT PASSWORD DIALOG */}
      <Dialog 
        open={forgotOpen} 
        onClose={handleForgotClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Key color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Forgot Password
            </Typography>
          </Box>
          <IconButton onClick={handleForgotClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {!foundUser ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your registered email address to retrieve your password.
              </Typography>

              {forgotError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {forgotError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                disabled={forgotLoading}
                placeholder="your@email.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Account found! Here are your login credentials:
              </Alert>

              <Card variant="outlined" sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      NAME
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {foundUser.name}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      EMAIL
                    </Typography>
                    <Typography variant="body1">
                      {foundUser.email}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      ROLE
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={foundUser.role?.toUpperCase() || 'USER'} 
                        color="primary" 
                        size="small" 
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      PASSWORD / PIN
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mt: 0.5,
                      bgcolor: 'background.paper',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'divider'
                    }}>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold" 
                        color="success.main"
                        sx={{ 
                          fontFamily: 'monospace',
                          letterSpacing: 1,
                          flex: 1
                        }}
                      >
                        {foundUser.password}
                      </Typography>
                      <Button
                        size="small"
                        variant={copied ? "contained" : "outlined"}
                        color={copied ? "success" : "primary"}
                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                        onClick={handleCopyPassword}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                ⚠️ For security reasons, please change your password after login.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {!foundUser ? (
            <>
              <Button 
                onClick={handleForgotClose} 
                color="inherit"
                disabled={forgotLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleForgotSubmit}
                variant="contained"
                disabled={forgotLoading || !forgotEmail.trim()}
                startIcon={forgotLoading ? <CircularProgress size={16} /> : <Key />}
              >
                {forgotLoading ? 'Searching...' : 'Find Account'}
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={handleForgotClose} 
                color="inherit"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setForm({ email: foundUser.email, password: foundUser.password });
                  handleForgotClose();
                }}
                variant="contained"
                color="success"
                startIcon={<LoginIcon />}
              >
                Auto-Fill & Login
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}